import React, { useCallback, useMemo, useState } from 'react';
import { RefreshControl, View } from 'react-native';
import { useNavigation } from '@react-navigation/native';

import { useUserContext } from '../../../common/utils/context';
import { getLocalTime } from '../../../common/utils/time/time';
import { formatDate } from '../../../common/utils/utils';
import Text from '../../components/text/Text';
import {
	Card,
	EmptyState,
	PrimaryButton,
	ProgressBar,
	Screen,
	SecondaryButton,
} from '../../design/components';
import { useRefreshPage } from '../../hooks';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import RunStopsList from './components/RunStopsList';
import RunSwitcher from './components/RunSwitcher';
import { useDriverDay, useDriverShell } from './DriverDayProvider';
import DriverTrainingBanner from './DriverTrainingBanner';
import { DRIVER_ROUTE, RUN_LOAD_ERROR } from './driverRoutes';
import BriefSheet from './startOfDay/BriefSheet';
import useBriefRead from './startOfDay/useBriefRead';
import { getStopPositionName, getStopSiteName } from './stopDetails';
import useDriverDayActions from './useDriverDayActions';

const getVehicleLabel = (vehicle) => {
	if (!vehicle) {
		return null;
	}

	const name = vehicle.name || '';
	// Some vehicle names already end with the registration.
	return vehicle.registration && !name.includes(vehicle.registration)
		? `${name} (${vehicle.registration})`.trim()
		: name;
};

/**
 * On the run: run header and progress, Run brief / Route map / Reorder, the
 * Now card (the stop actually in progress, else the next planned stop) and
 * every stop grouped by site (RunStopsList). Live through the shared driver day.
 */
const RunListView = () => {
	const navigation = useNavigation();
	const styles = useThemedStyles(createStyles);
	const { tenant, tenantSettings } = useUserContext();
	const shell = useDriverShell();
	const day = useDriverDay();
	const actions = useDriverDayActions(day);
	const [refreshPage, isRefreshing] = useRefreshPage(day.refresh);
	const [isBriefOpen, setIsBriefOpen] = useState(false);
	const {
		serviceRun,
		sortedDockets,
		isDocketDone,
		nowDocket,
		nextAction,
		completedStopsCount,
		allStopsComplete,
		allStopsHandled,
		couldNotCompleteDockets,
		reorderableStops,
		runStarted,
		trainingOffline,
		isOnline,
	} = day;
	const { markRead: markBriefRead } = useBriefRead(serviceRun, day.isTraining);
	const closeBrief = useCallback(() => {
		markBriefRead();
		setIsBriefOpen(false);
	}, [markBriefRead]);
	const { acknowledgeBrief: acknowledgeRunBrief } = actions;
	const acknowledgeBrief = useCallback(async () => {
		if (await acknowledgeRunBrief()) {
			setIsBriefOpen(false);
		}
	}, [acknowledgeRunBrief]);

	const nowNextStep = useMemo(() => (
		nowDocket ? day.buildStopSteps(nowDocket).find(step => step.required !== false && !step.done) : null
	), [day, nowDocket]);

	const totalCount = sortedDockets.length;
	const canReorder = reorderableStops.length > 1;
	const openReorder = () => {
		if (actions.requireTrainingConnection()) {
			navigation.push(DRIVER_ROUTE.REORDER_STOPS);
		}
	};

	const renderNowCard = () => {
		if (!totalCount) {
			return null;
		}

		if (allStopsComplete) {
			return (
				<Card tone="success" style={styles.nowCard}>
					<Text style={styles.overlineSuccess}>Run complete</Text>
					<Text style={styles.nowTitle}>All stops done</Text>
					<Text style={styles.successDetail}>{`${totalCount} of ${totalCount} ${totalCount === 1 ? 'machine' : 'machines'} done.`}</Text>
					{shell.isMyDayEnabled && (
						<PrimaryButton label="Go to end of day" onPress={() => navigation.navigate(DRIVER_ROUTE.END_OF_DAY)} />
					)}
				</Card>
			);
		}

		// Every stop is done or marked "Couldn't complete": the driver moves on to end of day.
		if (allStopsHandled) {
			return (
				<Card tone="current" style={styles.nowCard}>
					<Text style={styles.overline}>No stops left</Text>
					<Text style={styles.nowTitle}>Ready for end of day</Text>
					<Text style={styles.nowDetail}>
						{`${completedStopsCount} of ${totalCount} ${totalCount === 1 ? 'machine' : 'machines'} done. ${couldNotCompleteDockets.length} couldn't complete.`}
					</Text>
					{shell.isMyDayEnabled && (
						<PrimaryButton label="Go to end of day" onPress={() => navigation.navigate(DRIVER_ROUTE.END_OF_DAY)} />
					)}
				</Card>
			);
		}

		if (!nowDocket) {
			return null;
		}

		// Tenants without My day have no Start of day; their runs start with the
		// first stop. Once the run is under way the stop work comes first.
		if (shell.isMyDayEnabled && nextAction?.type === 'start' && !runStarted) {
			return (
				<Card tone="current" style={styles.nowCard}>
					<Text style={styles.overline}>Before you head out</Text>
					<Text style={styles.nowTitle}>Finish start of day</Text>
					<Text style={styles.nowDetail}>{`Next step: ${nextAction.step.label}`}</Text>
					<PrimaryButton label="Go to start of day" onPress={() => navigation.navigate(DRIVER_ROUTE.START_OF_DAY)} />
				</Card>
			);
		}

		const siteName = getStopSiteName(nowDocket);
		if (shell.isMyDayEnabled && nextAction?.type === 'startRun') {
			const needsOnlineCheck = Boolean(serviceRun?.myDayPickTopUpStatus?.markedNotNeeded);
			return (
				<Card tone="current" style={styles.nowCard}>
					<Text style={styles.overline}>First stop</Text>
					{Boolean(siteName) && <Text style={styles.nowTitle}>{siteName}</Text>}
					<Text style={styles.nowPosition}>{getStopPositionName(nowDocket)}</Text>
					<Text style={styles.nowDetail}>Everything is ready. Start the run when you leave.</Text>
					<PrimaryButton
						label={needsOnlineCheck && !isOnline ? 'Connect to start service run' : 'Start service run'}
						onPress={actions.startServiceRun}
						loading={needsOnlineCheck ? actions.isConfirmingOnlineStep : actions.isConfirmingStep}
						disabled={trainingOffline || (needsOnlineCheck && !isOnline)}
					/>
				</Card>
			);
		}

		const isInProgress = Boolean(nowDocket.startedProcessing) || day.buildStopSteps(nowDocket).some(step => step.done);
		return (
			<Card tone="current" style={styles.nowCard}>
				<Text style={styles.overline}>Now</Text>
				{Boolean(siteName) && <Text style={styles.nowTitle}>{siteName}</Text>}
				<Text style={styles.nowPosition}>{getStopPositionName(nowDocket)}</Text>
				<Text style={styles.nowDetail}>
					{nowNextStep ? `Next step: ${nowNextStep.label}` : 'All steps done. Check the stop before you move on.'}
				</Text>
				<PrimaryButton
					label={isInProgress ? 'Resume stop' : 'Open stop'}
					onPress={() => actions.openStopDetails(nowDocket)}
				/>
			</Card>
		);
	};

	const renderBody = () => {
		if (!serviceRun) {
			return (
				<EmptyState
					icon="truck"
					title="No run today"
					message={day.upcomingRun
						? `Your next run: ${formatDate(day.upcomingRun.planned, 'DAY_MONTHWORD_YEAR')}${day.upcomingRun.route?.name ? ` · ${day.upcomingRun.route.name}` : ''}`
						: 'When a run is assigned to you, its stops appear here.'}
				/>
			);
		}

		return (
			<>
				<ProgressBar
					done={completedStopsCount}
					total={totalCount}
					label={`${completedStopsCount} of ${totalCount} ${totalCount === 1 ? 'machine' : 'machines'} done`}
					hint={totalCount > completedStopsCount ? `${totalCount - completedStopsCount} to go` : undefined}
				/>
				<View style={styles.buttons}>
					<SecondaryButton
						label="Run brief"
						onPress={() => setIsBriefOpen(true)}
						disabled={!serviceRun.brief}
						style={styles.button}
					/>
					<SecondaryButton label="Route map" onPress={actions.openFullRouteMap} style={styles.button} />
					<SecondaryButton
						label="Reorder"
						onPress={openReorder}
						disabled={!canReorder || trainingOffline}
						accessibilityHint={canReorder ? 'Changes the order of the stops still to do' : 'Fewer than two stops can still be moved'}
						style={styles.button}
					/>
				</View>
				{renderNowCard()}
				{totalCount
					? (
						<RunStopsList
							dockets={sortedDockets}
							isDocketDone={isDocketDone}
							nowDocketId={nowDocket?.id}
							onOpenStop={actions.openStopDetails}
						/>
					)
					: <EmptyState title="No stops" message="There are no stops on this run yet. See your manager if you are expecting some." />}
				<BriefSheet
					isVisible={isBriefOpen}
					onClose={closeBrief}
					serviceRun={serviceRun}
					requireAcknowledgement={Boolean(tenantSettings?.myDayRequireBriefAcknowledgement)}
					onAcknowledge={acknowledgeBrief}
					isAcknowledging={actions.isAcknowledgingBrief}
					disabled={trainingOffline}
				/>
			</>
		);
	};

	const runDate = serviceRun?.planned ? getLocalTime(tenant?.timezone, serviceRun.planned).format('ddd D MMM') : null;

	return (
		<Screen
			backLabel="Home"
			heading={serviceRun ? (serviceRun.route?.name || 'On the run') : 'On the run'}
			subheading={serviceRun ? [runDate, getVehicleLabel(serviceRun.vehicle)].filter(Boolean).join(' · ') : undefined}
			isLoading={day.isLoading && !serviceRun}
			error={day.loadError ? RUN_LOAD_ERROR : undefined}
			onRetry={day.refresh}
			banner={<DriverTrainingBanner />}
			refreshControl={<RefreshControl refreshing={isRefreshing} onRefresh={refreshPage} />}
		>
			<RunSwitcher runs={day.todaysRuns} activeRunId={serviceRun?.id} onSelectRun={day.setActiveRunId} />
			{renderBody()}
		</Screen>
	);
};

const createStyles = ({ ui: { colors, type, space } }) => ({
	buttons: {
		flexDirection: 'row',
		gap: space.sm,
	},
	button: {
		flex: 1,
		paddingHorizontal: space.xs,
	},
	nowCard: {
		gap: space.xs,
	},
	overline: {
		...type.overline,
		color: colors.accent,
	},
	overlineSuccess: {
		...type.overline,
		color: colors.onSuccessCard,
	},
	nowTitle: {
		...type.cardTitle,
		color: colors.text,
	},
	nowPosition: {
		...type.rowTitleSemibold,
		color: colors.text,
	},
	successDetail: {
		...type.secondary,
		color: colors.onSuccessCard,
		marginBottom: space.sm,
	},
	nowDetail: {
		...type.secondary,
		color: colors.currentSubtle,
		marginBottom: space.sm,
	},
});

export default RunListView;
